import { useEffect, useState } from 'react';
import api from '../admin/api';

const WINDOWS = [
  { label: 'Online Now', minutes: 5 },
  { label: 'Last 30 Min', minutes: 30 },
  { label: 'Last 24 Hours', minutes: 1440 },
  { label: 'Last 7 Days', minutes: 10080 },
];

const timeAgo = (ts) => {
  const s = Math.max(0, (Date.now() - new Date(ts)) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const fmtDuration = (sec) => {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
};

const VisitorsPage = () => {
  const [minutes, setMinutes] = useState(30);
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/admin/visitors?minutes=${minutes}`);
        if (alive) setVisitors(data);
      } finally {
        if (alive) setLoading(false);
      }
    };
    setLoading(true);
    load();
    const t = setInterval(load, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [minutes]);

  return (
    <div data-testid="admin-visitors" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-serif text-3xl font-medium text-ink">Visitors</h1>
        <div className="flex flex-wrap gap-2">
          {WINDOWS.map((w) => (
            <button key={w.minutes} data-testid={`visitor-window-${w.minutes}`} onClick={() => setMinutes(w.minutes)}
              className={`border px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${minutes === w.minutes ? 'border-bronze bg-bronze text-paper' : 'border-line text-muted2 hover:text-ink'}`}>
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto border border-line bg-paper">
        <table className="w-full min-w-[900px] text-left text-sm">
          <thead>
            <tr className="border-b border-line text-[10px] font-semibold uppercase tracking-[0.2em] text-muted2">
              <th className="px-5 py-3.5">Visitor</th>
              <th className="px-5 py-3.5">Current Page</th>
              <th className="px-5 py-3.5">Pages</th>
              <th className="px-5 py-3.5">Time on Site</th>
              <th className="px-5 py-3.5">Entry Page</th>
              <th className="px-5 py-3.5">Source</th>
              <th className="px-5 py-3.5">Device</th>
              <th className="px-5 py-3.5">Location</th>
              <th className="px-5 py-3.5">Last Active</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {visitors.map((v) => (
              <tr key={v.id} data-testid={`visitor-row-${v.id.slice(0, 8)}`} className="align-top">
                <td className="px-5 py-3.5">
                  <span className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${v.online ? 'bg-green-600' : 'bg-line'}`} title={v.online ? 'Online now' : 'Offline'} />
                    <span className="font-mono text-xs text-ink/70">#{v.id.slice(0, 8)}</span>
                  </span>
                </td>
                <td className="max-w-[180px] truncate px-5 py-3.5 font-medium text-ink">{v.current_page}</td>
                <td className="px-5 py-3.5 text-muted2">{v.pages?.length || 1}</td>
                <td className="px-5 py-3.5 text-muted2">{fmtDuration(v.time_on_site_sec || 0)}</td>
                <td className="max-w-[160px] truncate px-5 py-3.5 text-muted2">{v.entry_page}</td>
                <td className="max-w-[140px] truncate px-5 py-3.5 text-muted2">{v.referrer || 'Direct'}</td>
                <td className="px-5 py-3.5 text-muted2">{v.device}</td>
                <td className="px-5 py-3.5 text-muted2">{v.location || '—'}</td>
                <td className="px-5 py-3.5 text-muted2">{timeAgo(v.last_seen)}</td>
              </tr>
            ))}
            {!loading && visitors.length === 0 && (
              <tr><td colSpan={9} className="px-5 py-10 text-center text-muted2">No visitors in this window yet. Tracking is live — visits will appear here as they happen.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted2">Anonymous session IDs only — no personal data is collected. Refreshes automatically every 10 seconds.</p>
    </div>
  );
};

export default VisitorsPage;
